/**
 * Hook for loading all locations across projects, for the cross-project task
 * board filters. Mirrors how usePlots/useSections load every row with a high
 * limit; the cascading-filter logic narrows the visible options client-side.
 *
 * NOTE: capped at `limit` rows — if a workspace exceeds this, the Sijainti
 * dropdown is truncated. Matches the existing plot/section loaders' behaviour.
 */
import { gql, useQuery } from "@apollo/client";

export interface AllLocationNode {
  id: string;
  name: string;
  parentLocationId: string | null;
  plotId: string | null;
  projectId: string;
  hierarchyPath: string[];
}

interface AllLocationsResult {
  locations: { items: AllLocationNode[] };
}

const GET_ALL_LOCATIONS = gql`
  query GetAllLocations($limit: Int) {
    locations(limit: $limit) {
      items {
        id
        name
        parentLocationId
        plotId
        projectId
        hierarchyPath
      }
    }
  }
`;

export function useAllLocations() {
  const { data, loading, error } = useQuery<AllLocationsResult>(
    GET_ALL_LOCATIONS,
    {
      variables: { limit: 5000 },
      fetchPolicy: "cache-and-network",
    },
  );
  return {
    locations: (data?.locations.items ?? []) as AllLocationNode[],
    loading,
    error,
  };
}
